<script setup lang="ts">
import { trustpilotReviewDate } from '~/composables/trustpilotReviewDate'

interface Review {
  author: string
  ratingValue: number
  body: string
  datePublished: string
}

interface TrustpilotProductSummary {
  aggregate: { ratingValue: number, reviewCount: number } | null
  profileUrl?: string | null
  businessUnitId?: string
  sku?: string
}

const props = defineProps<{ data?: TrustpilotProductSummary | null }>()

const aggregate = computed(() => props.data?.aggregate ?? null)
const profileUrl = computed(() =>
  props.data?.profileUrl || 'https://www.trustpilot.com/review/180medical.com')

const { data: reviewData, status: reviewStatus } = useLazyFetch('/api/trustpilot/product-reviews', {
  query: { businessUnitId: props.data?.businessUnitId, sku: props.data?.sku },
  key: `tp-product-reviews-${props.data?.businessUnitId ?? ''}-${props.data?.sku ?? ''}`,
  server: false,
  immediate: !!(props.data?.businessUnitId && props.data?.sku),
})

const REVEAL_STEP = 10

const laterPages = ref<Review[]>([])
const fetchedPage = ref(1)
const loadingMore = ref(false)
const upstreamHasMore = ref(false)
const visibleCount = ref(REVEAL_STEP)

const reviews = computed<Review[]>(() => [...(reviewData.value?.reviews ?? []), ...laterPages.value])
const visibleReviews = computed<Review[]>(() => reviews.value.slice(0, visibleCount.value))
const showReviews = computed(() => reviewStatus.value === 'success' && reviews.value.length > 0)

watch(reviewData, (data) => {
  upstreamHasMore.value = !!data?.hasMore
})

const canLoadMore = computed(() =>
  visibleCount.value < reviews.value.length || upstreamHasMore.value)

const scroller = ref<HTMLElement | null>(null)
const scrollable = ref(false)

function measureScroller() {
  const el = scroller.value
  scrollable.value = !!el && el.scrollHeight - el.clientHeight > 1
}

useResizeObserver(scroller, measureScroller)
watch(visibleReviews, () => nextTick(measureScroller))

async function loadMore() {
  visibleCount.value += REVEAL_STEP
  if (visibleCount.value <= reviews.value.length || !upstreamHasMore.value || loadingMore.value)
    return

  loadingMore.value = true
  try {
    const next = fetchedPage.value + 1
    const res = await $fetch<{ reviews?: Review[], hasMore?: boolean }>('/api/trustpilot/product-reviews', {
      query: { businessUnitId: props.data?.businessUnitId, sku: props.data?.sku, page: next },
    })
    fetchedPage.value = next
    laterPages.value.push(...(res?.reviews ?? []))
    upstreamHasMore.value = !!res?.hasMore
  }
  catch {
    upstreamHasMore.value = false
  }
  finally {
    loadingMore.value = false
  }
}

const currentYear = new Date().getUTCFullYear()

const now = ref<number | null>(null)
let timeAgoTimer: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  now.value = Date.now()
  timeAgoTimer = setInterval(() => {
    now.value = Date.now()
  }, 60_000)
})
onBeforeUnmount(() => {
  if (timeAgoTimer)
    clearInterval(timeAgoTimer)
})

function isoDate(iso: string) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

const displayDate = (iso: string) => trustpilotReviewDate(iso, now.value, currentYear)
const absoluteDate = (iso: string) => trustpilotReviewDate(iso, null, currentYear)
</script>

<template>
  <section
    v-if="aggregate || showReviews"
    class="flex flex-col gap-5"
    data-testid="cookieless-trustpilot-reviews"
  >
    <div
      v-if="aggregate"
      class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
    >
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
        <TrustpilotStars :rating="aggregate.ratingValue" />
        <p class="text-med-deep-navy">
          <strong>{{ aggregate.ratingValue.toFixed(1) }}</strong>
          <span class="text-med-dark-gray"> / 5 &middot; {{ aggregate.reviewCount.toLocaleString() }} reviews</span>
        </p>
      </div>
      <a
        class="no-underline"
        :href="profileUrl"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="180 Medical on Trustpilot"
      >
        <TrustpilotLogo />
      </a>
    </div>

    <p
      v-if="showReviews"
      class="text-sm text-med-dark-gray"
      data-testid="cookieless-trustpilot-review-disclosure"
    >
      Showing our latest reviews
    </p>

    <section
      v-if="showReviews"
      ref="scroller"
      class="max-h-[550px] overflow-y-auto overscroll-contain pr-1"
      :tabindex="scrollable ? 0 : undefined"
      aria-label="Reviews for this product"
      data-testid="cookieless-trustpilot-review-scroller"
    >
      <ul class="m-0 flex list-none flex-col p-0">
        <li
          v-for="(r, i) in visibleReviews"
          :key="i"
          class="flex flex-col gap-2 border-t border-med-light-gray py-5 first:border-t-0 first:pt-0"
        >
          <p class="text-sm">
            <span class="font-semibold text-med-deep-navy">{{ r.author }}</span>
            <template v-if="absoluteDate(r.datePublished)">
              <span class="text-med-dark-gray">, <time
                :datetime="isoDate(r.datePublished)"
                :title="absoluteDate(r.datePublished)"
              >{{ displayDate(r.datePublished) }}</time></span>
            </template>
          </p>

          <TrustpilotStars
            :rating="r.ratingValue"
            size="sm"
          />

          <p
            v-if="r.body"
            class="text-med-deep-navy"
          >
            {{ r.body }}
          </p>
        </li>
      </ul>
    </section>

    <button
      v-if="showReviews && canLoadMore"
      type="button"
      class="inline-flex w-fit cursor-pointer items-center gap-2 rounded-full border border-med-deep-navy bg-transparent px-5 py-2 text-sm font-semibold text-med-deep-navy hover:bg-med-deep-navy hover:text-white disabled:cursor-default disabled:opacity-60"
      :aria-busy="loadingMore"
      :disabled="loadingMore"
      data-testid="cookieless-trustpilot-load-more"
      @click="loadMore"
    >
      {{ loadingMore ? 'Loading�' : 'Load more reviews' }}
    </button>

    <a
      class="inline-flex w-fit items-center gap-2 text-sm text-med-deep-navy underline underline-offset-2 hover:text-med-dark-gray"
      :href="profileUrl"
      target="_blank"
      rel="noopener noreferrer"
    >
      See our reviews on
      <TrustpilotLogo size="sm" />
    </a>
  </section>
</template>

<style scoped>
li :deep(.tp-stars__tile rect) {
  transition: filter 0.15s ease;
}
li:hover :deep(.tp-stars__tile rect) {
  filter: brightness(0.85);
}
</style>
